import React from 'react';
import { useAsync } from 'react-use';
import { useDebounce } from 'use-debounce';
import { useIntl } from 'react-intl';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useActiveUserId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import SubjectSearchService from '@lifeomic/phc-web-toolkit/src/services/SubjectSearchService';
import { getNameFromFHIRRecord } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '@lifeomic/phc-web-toolkit/src/hooks/useLocalStorageState';

import {
  getRecentConversationKey,
  useRecentConversations,
} from './useRecentConversations';
import {
  CLINICIANS_AGENT_USERNAME,
  POPULATION_AGENT_USERNAME,
} from '../constants';
import { PatientSuggestion } from '../components/ChatDockPanels';
import {
  ConversationEntry,
  ensurePopulationConversationId,
  FhirSubjectSearch,
  resolvePatientIdFromConversation,
} from '../components/chatDockUtils';
import { chatDockMessages } from '../components/chatDockMessages';

const subjectSearchService = new SubjectSearchService();

export const OPEN_CHAT_KEYS_STORAGE_KEY = 'zoriOpenChatKeysV1';

export const getConversationParticipantKey = ({
  userIds,
}: {
  userIds?: string[];
}) => (userIds?.length ? [...userIds].sort().join('|') : undefined);

export const applyConversationTitleOverride = (
  conversation: ConversationEntry,
  conversationTitleOverrides: Record<string, string>,
) => {
  const directTitleOverride =
    conversationTitleOverrides[conversation.key]?.trim();
  const participantKey = getConversationParticipantKey(conversation);
  const participantTitleOverride = participantKey
    ? conversationTitleOverrides[participantKey]?.trim()
    : undefined;
  const titleOverride = directTitleOverride || participantTitleOverride;
  if (!titleOverride || titleOverride === conversation.title?.trim()) {
    return conversation;
  }
  return {
    ...conversation,
    title: titleOverride,
  };
};

export const useZoriChatConversations = ({
  maxOpenConversations = 2,
  openChatKeysStorageKey = OPEN_CHAT_KEYS_STORAGE_KEY,
}: {
  maxOpenConversations?: number;
  openChatKeysStorageKey?: string;
} = {}) => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const { recentConversations, loading, hideConversation, unhideConversation } =
    useRecentConversations();
  const [isCreatingChat, setIsCreatingChat] = React.useState(false);
  const [conversationTitleOverrides, setConversationTitleOverrides] =
    React.useState<Record<string, string>>({});
  const [openConversationKeys, setOpenConversationKeys] = React.useState<
    string[]
  >(() => {
    if (!openChatKeysStorageKey) return [];
    const stored =
      safeGetPersistedValue<string[]>(openChatKeysStorageKey) || [];
    return stored.filter(key => typeof key === 'string' && key.length > 0);
  });
  const [localConversationMap, setLocalConversationMap] = React.useState<
    Record<string, ConversationEntry>
  >({});
  const activeUserId = useActiveUserId();
  const activeProjectId = useActiveProjectId();
  const activeProjectCohortId = useActiveProjectCohortId();
  const maskedMode = useMaskedMode();
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedPatient, setSelectedPatient] =
    React.useState<PatientSuggestion | null>(null);
  const [debouncedFilter] = useDebounce(searchValue, 500);
  const [fhirSubjectSearch] = useService(
    subjectSearchService.fhirSubjectSearch,
    {
      id: 'zori-chat-subject-search',
      name: 'zori-chat-subject-search',
      log: { start: true, success: true, error: true },
      rethrowErrors: true,
    },
  );

  const applyTitleOverride = React.useCallback(
    (conversation: ConversationEntry) =>
      applyConversationTitleOverride(conversation, conversationTitleOverrides),
    [conversationTitleOverrides],
  );

  const recentConversationsWithTitleOverrides = React.useMemo(
    () => recentConversations.map(applyTitleOverride),
    [applyTitleOverride, recentConversations],
  );

  const openConversations = React.useMemo(
    () =>
      openConversationKeys
        .map(key => {
          const recent = recentConversationsWithTitleOverrides.find(
            conversation => conversation.key === key,
          );
          const local = localConversationMap[key];
          return recent || (local ? applyTitleOverride(local) : local);
        })
        .filter((conversation): conversation is ConversationEntry =>
          Boolean(conversation),
        ),
    [
      applyTitleOverride,
      localConversationMap,
      openConversationKeys,
      recentConversationsWithTitleOverrides,
    ],
  );

  React.useEffect(() => {
    if (openChatKeysStorageKey) {
      safePersistValue(openChatKeysStorageKey, openConversationKeys);
    }
  }, [openChatKeysStorageKey, openConversationKeys]);

  const { value: patientResults, loading: isSearching } = useAsync(async () => {
    if (maskedMode || !debouncedFilter) return [];
    const trimmed = debouncedFilter.trim();
    if (trimmed.length < 3) return [];
    const res = await fhirSubjectSearch({
      projectId: activeProjectId,
      body: {
        name: trimmed.split(/\s+/),
      },
    });
    if (res.data) {
      return res.data.entry.map(entry => entry.resource);
    }
    return [];
  }, [debouncedFilter, maskedMode, activeProjectId]);

  const patientSuggestions = React.useMemo(() => {
    const patients = (patientResults || []) as fhir3.Patient[];
    return patients
      .map(patient => {
        const name = getNameFromFHIRRecord(patient.name);
        return {
          id: patient.id!,
          display: `${name.given} ${name.family}`.trim(),
          patient,
        };
      })
      .filter(patient => patient.id && patient.display);
  }, [patientResults]);

  const renderConversationPreview = React.useCallback(
    (timestamp?: string) => {
      if (!timestamp) return null;
      const date = new Date(timestamp);
      return `${formatDate(date, {
        month: 'short',
        day: 'numeric',
      })} ${formatTime(date, { hour: 'numeric', minute: '2-digit' })}`;
    },
    [formatDate, formatTime],
  );

  const openConversation = React.useCallback(
    (entry: {
      conversationId?: string;
      patientId?: string;
      title?: string;
      userIds?: string[];
    }) => {
      const resolvedPatientId = resolvePatientIdFromConversation({
        patientId: entry.patientId,
        userIds: entry.userIds,
      });
      const userIds =
        resolvedPatientId && activeUserId
          ? [
              activeUserId,
              CLINICIANS_AGENT_USERNAME,
              `scope?subject=${resolvedPatientId}`,
            ]
          : entry.userIds?.length
          ? entry.userIds
          : undefined;
      const normalizedUserIds = ensurePopulationConversationId(userIds);
      const normalizedEntry = {
        conversationId: entry.conversationId || '',
        patientId: resolvedPatientId,
        title: entry.title,
        userIds: normalizedUserIds,
      };
      const key =
        normalizedEntry.conversationId ||
        getRecentConversationKey(normalizedEntry);
      if (!key) return;
      unhideConversation(entry.conversationId);
      setLocalConversationMap(prev => ({
        ...prev,
        [key]: {
          key,
          conversationId: normalizedEntry.conversationId || undefined,
          patientId: normalizedEntry.patientId,
          title: normalizedEntry.title,
          userIds: normalizedEntry.userIds,
        },
      }));
      setIsCreatingChat(false);
      setOpenConversationKeys(prev => {
        const next = [key, ...prev.filter(existing => existing !== key)];
        return next.slice(0, maxOpenConversations);
      });
    },
    [activeUserId, maxOpenConversations, unhideConversation],
  );

  const handleCreateChatClick = React.useCallback(() => {
    setIsCreatingChat(true);
    setSearchValue('');
    setSelectedPatient(null);
  }, []);

  const handleHideConversation = React.useCallback(
    (conversation: ConversationEntry) => {
      hideConversation(conversation.conversationId);
      setOpenConversationKeys(keys =>
        keys.filter(key => key !== conversation.key),
      );
      setLocalConversationMap(prev => {
        if (!prev[conversation.key]) return prev;
        const next = { ...prev };
        delete next[conversation.key];
        return next;
      });
    },
    [hideConversation],
  );

  const setConversationDisplayTitle = React.useCallback(
    (conversationKey: string, title: string) => {
      const trimmedTitle = title.trim();
      if (!conversationKey || !trimmedTitle) return;
      const participantKey = getConversationParticipantKey(
        localConversationMap[conversationKey] || {},
      );
      setConversationTitleOverrides(prev => {
        if (
          prev[conversationKey] === trimmedTitle &&
          (!participantKey || prev[participantKey] === trimmedTitle)
        ) {
          return prev;
        }
        return {
          ...prev,
          [conversationKey]: trimmedTitle,
          ...(participantKey ? { [participantKey]: trimmedTitle } : {}),
        };
      });
      setLocalConversationMap(prev => {
        const existing = prev[conversationKey];
        if (!existing || existing.title === trimmedTitle) return prev;
        return {
          ...prev,
          [conversationKey]: {
            ...existing,
            title: trimmedTitle,
          },
        };
      });
    },
    [localConversationMap],
  );

  const handleSuggestionSelected = React.useCallback(
    (selected: {
      display: string;
      suggestionValue?: string;
      suggestion: PatientSuggestion;
    }) => {
      if (!activeUserId) return;
      const { suggestion, display } = selected;
      setSelectedPatient(suggestion);
      const userIds = [
        activeUserId,
        CLINICIANS_AGENT_USERNAME,
        `scope?subject=${suggestion.id}`,
      ];
      openConversation({
        patientId: suggestion.id,
        title: display || suggestion.display,
        userIds,
      });
    },
    [activeUserId, openConversation],
  );

  const handleStartWithoutPatient = React.useCallback(() => {
    if (!activeUserId) return;
    const userIds = [
      activeUserId,
      POPULATION_AGENT_USERNAME,
      `scope?conversation_id=${Date.now()}`,
    ];
    openConversation({
      title: formatMessage(chatDockMessages.populationChatTitle),
      userIds,
    });
  }, [activeUserId, formatMessage, openConversation]);

  return {
    activeProjectCohortId,
    activeProjectId,
    activeUserId,
    fhirSubjectSearch: fhirSubjectSearch as FhirSubjectSearch,
    handleCreateChatClick,
    handleHideConversation,
    handleStartWithoutPatient,
    handleSuggestionSelected,
    isCreatingChat,
    isSearching,
    loading,
    localConversationMap,
    maskedMode,
    openConversation,
    openConversationKeys,
    openConversations,
    patientSuggestions,
    recentConversations: recentConversationsWithTitleOverrides,
    renderConversationPreview,
    searchValue,
    selectedPatient,
    setConversationDisplayTitle,
    setIsCreatingChat,
    setLocalConversationMap,
    setOpenConversationKeys,
    setSearchValue,
  };
};
